import React, { memo, useMemo, useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../../constants/styles';
import moment from 'moment';
import { EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY } from '../../../constants/eventTypes';

import { dateIsPast, dateTimeIsPast, nowDate } from '../../components/schedule_cal';
import AppointmentsCal from './appointments_cal';

const AppointmentCalPatient = memo((props) => {
  const [selectedDate, setSelectedDate] = useState(null);
  useEffect(() => {
    props.onSelectedDate?.(selectedDate);
    props.onSelectedDateIsAvailable?.(!!canSelectSlot(selectedDate));
  }, [selectedDate, canSelectSlot, props.onSelectedDate, props.onSelectedDateIsAvailable]);

  const baseConfig = props.eventTypesConfig || {};

  const schedulingWindowDays = baseConfig?.eventTypeSchedulingWindows?.[props.eventType] || -1;
  const futureDateLimit = useMemo(() => {
    return moment()
      .startOf('day')
      .add(schedulingWindowDays + 1, 'days')
      .toDate();
  }, [schedulingWindowDays]);
  const isTooFarInFuture = useCallback(
    (date) => {
      return date >= futureDateLimit;
    },
    [schedulingWindowDays]
  );

  const hasAvailability = useCallback(
    (date) => {
      const firstAvailable = (props.slotAvailability?.available || []).find((dStr) => {
        const isDateMatch = dStr.split('T')[0] === moment(date).format('YYYY-MM-DD');
        const isTimeInFuture = !dateTimeIsPast(dStr, props.timeZone);
        return isDateMatch && isTimeInFuture;
      });
      return !!firstAvailable;
    },
    [props.slotAvailability, props.timeZone]
  );

  const findNextAvailableDateFrom = useCallback(
    (date) => {
      const dateStr = (props.slotAvailability?.available || []).find((dStr) => {
        const d = moment.tz(dStr, props.timeZone);
        return d.isSameOrAfter(moment(date));
      });
      const dateMoment = dateStr ? moment(dateStr.split('T')[0]) : null;
      if (!dateMoment || isTooFarInFuture(dateMoment.toDate())) return null;
      return {
        label: dateMoment.format('MMMM DD, YYYY'),
        date: dateMoment.toDate(),
      };
    },
    [props.slotAvailability, isTooFarInFuture]
  );
  const nextAvailableDate = useMemo(() => {
    return findNextAvailableDateFrom(new Date());
  }, [findNextAvailableDateFrom]);
  useEffect(() => {
    setSelectedDate(nextAvailableDate?.date);
  }, [nextAvailableDate]);

  const firstAvailableDateFrom = useCallback(
    (date) => {
      if (!nextAvailableDate?.date) return date;
      if (moment(date).isSame(moment(nextAvailableDate.date), 'month')) {
        return nextAvailableDate.date;
      } else if (moment(date).isBefore(moment(nextAvailableDate.date), 'month')) {
        return date;
      } else {
        const nextAvailableFromCurrentMonth = findNextAvailableDateFrom(date)?.date;
        if (moment(date).isSame(moment(nextAvailableFromCurrentMonth), 'month')) {
          return nextAvailableFromCurrentMonth;
        }
        return date;
      }
    },
    [findNextAvailableDateFrom, nextAvailableDate]
  );

  const canSelectSlot = useCallback(
    (start) => {
      return !dateIsPast(start) && !isTooFarInFuture(start) && hasAvailability(start);
    },
    [hasAvailability, isTooFarInFuture]
  );

  const calDisplayTextAsOffRangeForDate = useCallback(
    (date) => {
      return !canSelectSlot(date);
    },
    [canSelectSlot]
  );
  const calCellBGClassForDate = useCallback(
    (date) => {
      let className = '';
      if (!canSelectSlot(date)) {
        className += ' rbc-disabled-bg';
      }
      return className;
    },
    [canSelectSlot]
  );

  const goToNextAvailableDate = useCallback(() => {
    if (!nextAvailableDate?.date) return;
    setSelectedDate(nextAvailableDate?.date);
  }, [nextAvailableDate]);

  const appointmentSlotAvailable = nextAvailableDate && !isTooFarInFuture(nextAvailableDate.date);

  // the first time goToNextAvailableDate is defined (when nextAvailableDate is set)
  // focus the calendar on the next available date.
  useEffect(() => {
    goToNextAvailableDate();
  }, [goToNextAvailableDate]);

  const arrowEnabled = useCallback(
    (monthDate, direction) => {
      if (!appointmentSlotAvailable) return false;

      const today = moment().startOf('day');
      const date = moment(monthDate);

      if (direction === -1) {
        const lastDayPrevMonth = moment(date).subtract(1, 'month').endOf('month');
        return lastDayPrevMonth.isSameOrAfter(today);
      }
      if (direction === 1) {
        const firstDayNextMonth = moment(date).add(1, 'month').startOf('month');
        return !isTooFarInFuture(firstDayNextMonth.toDate());
      }
      return true;
    },
    [appointmentSlotAvailable, isTooFarInFuture]
  );

  // const appointmentSlotAvailable = false;
  const allDayBlocked = !canSelectSlot(selectedDate);
  return (
    <View>
      {!appointmentSlotAvailable ? (
        <Text style={styles.nextAvailableText}>
          There are no available appointments in the next {schedulingWindowDays} days.
        </Text>
      ) : (
        <Pressable onPress={goToNextAvailableDate}>
          <Text style={styles.nextAvailableText}>
            The next available appointment is{'  '}
            <Text style={styles.noAppointmentsNextDateText}>{nextAvailableDate?.label}</Text>
          </Text>
        </Pressable>
      )}
      <View style={[styles.paneWrapper, { flex: 1 }]}>
        <AppointmentsCal
          calContainerStyle={THEME.shadowSubtle}
          firstAvailableDateFrom={firstAvailableDateFrom}
          selectedDate={selectedDate}
          onSelectedDate={setSelectedDate}
          onTimesChange={props.onTimesChange}
          events={null}
          calDisplayTextAsOffRangeForDate={calDisplayTextAsOffRangeForDate}
          calCellBGClassForDate={calCellBGClassForDate}
          slotAvailability={props.slotAvailability}
          eventType={props.eventType}
          multi={false}
          disablePastSelect={true}
          canSelectSlot={canSelectSlot}
          fetchStatus={props.aptsSlotAvailabilityStatus}
          arrowEnabled={arrowEnabled}
          hideTimes={[EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(props.eventType)}
          timeZone={props.timeZone}
        />
        {!appointmentSlotAvailable && (
          <View style={styles.allDayBlockedContainer}>
            <Text style={styles.noAppointmentsText}>
              There are no available appointments in the next {schedulingWindowDays} days.
            </Text>
          </View>
        )}
        {!!allDayBlocked && !!appointmentSlotAvailable && (
          <Pressable onPress={goToNextAvailableDate} style={styles.allDayBlockedContainer}>
            <Text style={styles.noAppointmentsText}>
              The next available appointment is{' '}
              <Text style={styles.noAppointmentsNextDateText}>{nextAvailableDate?.label}</Text>
              {'. '}Please use the calendar above to request a different appointment time.
            </Text>
          </Pressable>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  paneWrapper: {
    flex: 1,
  },
  allDayBlockedContainer: {
    borderColor: COLOR.callToAction,
    borderWidth: 1,
    borderLeftWidth: 10,
    marginHorizontal: 30,
    paddingHorizontal: 30,
    paddingVertical: 15,
    marginBottom: 20,
  },
  nextAvailableText: {
    ...THEME.textRegular,
    textAlign: 'center',
    marginBottom: 15,
  },
  noAppointmentsText: {
    ...FONT.regular,
    fontSize: 14,
    color: COLOR.text,
  },
  noAppointmentsNextDateText: {
    ...THEME.linkStyle,
    ...FONT.semibold,
    textDecorationLine: 'underline',
  },
});

export default AppointmentCalPatient;
